
import React, { createContext, useContext } from 'react';
import { NodeTypeConfig, AIService, CallableProcess, NamedRuleSet, ErrorRule } from '../types';
import { NodePropertyConfig } from '../utils/propertyPanelRegistry';
import type { DirectoryService } from '../Controller';
import type { DmnService } from '../services/mockDmnService';

export interface EditorActions {
  openModal: (nodeId: string, tabId?: string) => void;
  activeNodeId?: string;
}

/**
 * Project-level error-handling ruleset library, exposed through context so that
 * NodeModal special components (which only receive {data,onChange,nodeType,
 * context}) and the standalone Library Manager share one source of truth.
 */
export interface ErrorRulesetLibrary {
  list: NamedRuleSet[];
  /** Create a named ruleset, returns the created entry (with assigned id). */
  create: (seed: { name: string; rules: ErrorRule[]; source?: string }) => NamedRuleSet;
  update: (id: string, patch: Partial<Pick<NamedRuleSet, 'name' | 'rules' | 'source'>>) => void;
  remove: (id: string) => void;
  /** How many nodes (across all processes) bind to this ruleset id. */
  usageCount: (id: string) => number;
}

export interface EditorServices {
  ai: AIService;
  directory: DirectoryService;
  dmn: DmnService;
  callableProcesses?: CallableProcess[];
}

export interface EditorConfig {
  nodeTypes: Record<string, NodeTypeConfig>;
  nodeCategories: string[];
  propertyRegistry: Record<string, NodePropertyConfig>;
  componentRegistry: Record<string, React.ComponentType<any>>;
  services: EditorServices;
  actions?: EditorActions;
  errorRulesets?: ErrorRulesetLibrary;
}

const EditorContext = createContext<EditorConfig | null>(null);

export const useEditorConfig = () => {
  const context = useContext(EditorContext);
  if (!context) throw new Error("useEditorConfig must be used within EditorProvider");
  return context;
};

export const EditorProvider: React.FC<{ config: EditorConfig; children: React.ReactNode }> = ({ config, children }) => {
  return <EditorContext.Provider value={config}>{children}</EditorContext.Provider>;
};
